import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { StateService } from '@adgate/common/services/state-service';
import { LogMessage } from '@adgate/common/types/log-message.type';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { switchMap } from 'rxjs';
import { Status } from '@adgate/common/types/status.type';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';
import { Creativity } from '@adgate/modules/streaming/types/creativity.type';
import { ProductionRequest } from '@adgate/modules/production/types/production-request.type';

@Component({
  selector: 'app-log-dialog-component',
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ObjectDescPipe
  ],
  templateUrl: './log-dialog-component.html',
  styleUrl: './log-dialog-component.scss',
})
export class LogDialogComponent implements OnInit
{
  public dialogData: LogDialogData = inject<LogDialogData>(MAT_DIALOG_DATA);
  public stateService: StateService = inject(StateService);
  public commonObjectService: CommonObjectService = inject(CommonObjectService);
  public dialogService: DialogService = inject(DialogService);
  private dialogHandle = inject(MatDialogRef<LogDialogComponent>);
  private cdr: ChangeDetectorRef = inject( ChangeDetectorRef );
  private fb = inject(FormBuilder);

  messageForm: FormGroup;
  logMessages!: LogMessage[];

  constructor()
  {
    this.messageForm = this.fb.group({
      message: ['', Validators.required]
    });
  }

  ngOnInit(): void
  {
    this.dialogService.showProgress();
    this.commonObjectService.getObjectHistory( this.dialogData.objectType, this.dialogData.objectId ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.logMessages = r.data;
        this.dialogService.hideProgress();
        this.cdr.detectChanges();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getObjectDescription(): string
  {
    switch(this.dialogData.objectType) {
      case Commercial.OBJECT_TYPE: return 'Commercial';
      case Creativity.OBJECT_TYPE: return 'Creativity';
      case ProductionRequest.OBJECT_TYPE: return 'Production Request'
      default: return 'Object';
    }
  }

  getTimelineDotClass(type: number): string
  {
    switch( type ) {
      case LogMessage.TYPE_USER: return 'tl-user';
      case LogMessage.TYPE_SYSTEM: return 'tl-system';
      case LogMessage.TYPE_UPLOAD: return 'tl-system';
      case LogMessage.TYPE_ERROR: return 'tl-error';
      case LogMessage.TYPE_SPECERROR: return 'tl-error';
      default: return 'tl-user';
    }
  }

  getTimelineIconClass(type: number): string
  {
    switch( type ) {
      case LogMessage.TYPE_USER: return 'fa-user';
      case LogMessage.TYPE_SYSTEM: return 'fa-robot';
      case LogMessage.TYPE_ERROR: return 'fa-triangle-exclamation';
      case LogMessage.TYPE_SPECERROR: return 'fa-triangle-exclamation';
      case LogMessage.TYPE_UPLOAD: return 'fa-cloud-arrow-up';
      default: return 'tl-user';
    }
  }

  onClose(): void
  {
    this.dialogHandle.close();
  }

  onSubmit(): void
  {
    if (this.messageForm.valid) {
      this.dialogService.showProgress();
      this.commonObjectService.addObjectHistory(this.dialogData.objectType, [this.dialogData.objectId], this.stateService.getUserName(), this.messageForm.get('message')?.value, LogMessage.TYPE_USER, false, [], false ).pipe(
        switchMap((r: RESTApplicationResponse) => {
          return this.commonObjectService.getObjectHistory( this.dialogData.objectType, this.dialogData.objectId );
        })
      ).subscribe({
        next: (r: RESTApplicationResponse) => {
          this.logMessages = r.data;
          this.dialogService.hideProgress();
          this.cdr.detectChanges();
          this.messageForm.reset();
        },
        error: (err: Error) => {
          this.dialogService.openErrorDialog(err.message);
          this.messageForm.reset();
        }
      });
    }
  }
}

export class LogDialogData 
{
  objectType!: number;
  objectId!: number;
  objectDescription!: string | null;
  objectStatus!: Status | null;
  allowAdd!: boolean;
}

